import { useState } from "react";
import { Loader2, CheckCircle2, Send } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { useLanguage } from "../lib/LanguageContext";
import { useAuth } from '@/lib/AuthContext';

export default function ContactForm() {
  const { language } = useLanguage();
  const isPl = language === "pl";
  const { user } = useAuth();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) return;

    setStatus("sending");
    try {
      const res = await base44.functions.invoke("sendContactMessage", {
        name,
        email,
        message,
        language,
      });
      if (!res.data?.ok) throw new Error("send failed");
      window.gtag?.('event', 'conversion', { send_to: 'AW-18403490920/8-AoCMuV1eUcEOj4u8dE' });
      setStatus("sent");
      setName("");
      setEmail("");
      setMessage("");
    } catch (err) {
      setStatus(err?.response?.status === 429 ? 'limited' : 'error');
    }
  };

  if (!user) return <div className="space-y-3 text-sm"><p className="text-muted-foreground">{isPl ? 'Aby chronić formularz przed spamem, wysyłanie wymaga zalogowania. Limit: jedna wiadomość na godzinę.' : 'To prevent spam, sign in to send a message. Limit: one message per hour.'}</p><button onClick={() => base44.auth.redirectToLogin(window.location.href)} className="min-h-11 px-4 rounded-lg bg-primary text-primary-foreground">{isPl ? 'Zaloguj się, aby napisać' : 'Sign in to send a message'}</button></div>;

  if (status === "sent") {
    return (
      <div className="flex flex-col items-center gap-2 py-8 text-center">
        <CheckCircle2 className="w-8 h-8 text-green-600" />
        <p className="text-foreground font-medium">
          {isPl ? "Wiadomość wysłana! Odpowiem najszybciej jak możliwe." : "Message sent! I'll get back to you as soon as possible."}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <label htmlFor="contact-name" className="block text-xs font-medium text-foreground mb-1">
          {isPl ? "Imię" : "Name"}
        </label>
        <input
          id="contact-name"
          type="text"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      <div>
        <label htmlFor="contact-email" className="block text-xs font-medium text-foreground mb-1">
          {isPl ? "Twój e-mail" : "Your email"}
        </label>
        <input
          id="contact-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      <div>
        <label htmlFor="contact-message" className="block text-xs font-medium text-foreground mb-1">
          {isPl ? "Wiadomość" : "Message"}
        </label>
        <textarea
          id="contact-message"
          required
          rows={4}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
        />
      </div>

      <p className="text-xs text-muted-foreground">{isPl ? 'Limit: jedna wiadomość na godzinę.' : 'Limit: one message per hour.'}</p>
      {status === 'limited' && <p role="alert" className="text-xs text-destructive">{isPl ? 'Wiadomość została już wysłana w tej godzinie. Spróbuj w następnej.' : 'A message has already been sent this hour. Try next hour.'}</p>}
      {status === "error" && (
        <p className="text-xs text-destructive">
          {isPl ? "Nie udało się wysłać wiadomości. Spróbuj ponownie później." : "Failed to send the message. Please try again later."}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "sending"}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 min-h-[44px] rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {status === "sending" ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Send className="w-4 h-4" />
        )}
        {isPl ? "Wyślij wiadomość" : "Send message"}
      </button>
    </form>
  );
}